import {createContext, useCallback, useContext, useEffect, useState} from 'react'
import _ from 'lodash'
import {Dimmer, Loader} from 'semantic-ui-react'
import {RequirePermission} from '@/features/auth'
import {MessageContext} from '@/features/errors'
import {CatalogSelectionContext} from '@/features/catalog-selection'
import {
  DataSourceTemplates,
  IDataSource,
  IDataSourceItem,
  IDataSourceItemMap,
  IDataSourceStats,
  LegacyCatalogContextWrap,
  useDataSourceStats,
} from '..'
import {DataSourceOverrideDialog} from '../components/override-dialog'
export * from './legacy'

export interface IDataSourcesContextWrapperProps {
  children: React.ReactElement
}

export interface IDataSourcesContext {
  isBusy: boolean
  isDialogOpen: boolean
  mergedItems: IDataSourceItem[]
  mergedItemsStats: IDataSourceStats
  sourceConfigs: IDataSource[]
  sourceData: DataSourceResultsMap
  activeCatalogName: string
  replaceSources: (sources: IDataSource[]) => void
  addSource: (source: IDataSource) => void
  showDialog: () => void
  skipLoadingSources: () => void
  /** @deprecated */
  ensureFetch: () => void
}

export type DataSourceResultsMap = {
  [key in keyof typeof DataSourceTemplates]: {
    [key: string]: IDataSourceItemMap
  }
}
type mergedScoreMap = {
  [from: string]: {
    ask: {
      [to: string]: number
    }
    similar: {
      [to: string]: number
    }
  }
}

const defaultSourceResultsMap: DataSourceResultsMap = {
  catalog: {},
  localFile: {},
  similar: {},
  ask: {},
  expression: {},
}

export const DataSourcesContext = createContext<IDataSourcesContext>({
  isBusy: false,
  isDialogOpen: false,
  mergedItems: [],
  mergedItemsStats: {
    categories: {},
    fields: {},
    fieldTypes: {},
    types: {},
    numbers: {},
  },
  sourceConfigs: [],
  sourceData: defaultSourceResultsMap,
  activeCatalogName: '',
  replaceSources: () => {
    throw new Error('not implemented')
  },
  addSource: () => {
    throw new Error('not implemented')
  },
  showDialog: () => {
    throw new Error('not implemented')
  },
  skipLoadingSources: () => {
    throw new Error('not implemented')
  },
  ensureFetch: () => {
    throw new Error('not implemented')
  },
})

export function DataSourcesContextWrapper(props: IDataSourcesContextWrapperProps) {
  const {children} = props

  const {activeCatalogName, setActiveCatalogName} = useContext(CatalogSelectionContext)

  const {logError} = useContext(MessageContext)

  const [isBusy, setIsBusyState] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [sourceConfigs, setSourceConfigs] = useState<IDataSource[]>([])
  const [sourceData, setSourceData] = useState<DataSourceResultsMap>(defaultSourceResultsMap)
  const [mergedItems, setMergedItems] = useState<IDataSourceItem[]>([])
  const [skipIsBusy, setSkipIsBusy] = useState(false)

  const setIsBusy = useCallback(
    (busy: boolean) => {
      if (skipIsBusy) return
      setIsBusyState(busy)
    },
    [skipIsBusy]
  )

  useEffect(() => {
    if (!(sourceConfigs && sourceConfigs.length > 0)) {
      setSourceData(defaultSourceResultsMap)
      return
    }
    async function f() {
      setIsBusy(true)
      const res: DataSourceResultsMap = {
        ...defaultSourceResultsMap,
      }
      for (let i = 0; i < sourceConfigs.length; i++) {
        const {sourceID, key} = sourceConfigs[i]
        const cachedData = sourceData[sourceID]?.[key]
        if (cachedData) {
          res[sourceID][key] = cachedData
          continue
        }
        try {
          const data = await fetchSourceConfig(sourceConfigs[i])
          if (!data) continue
          res[sourceID][key] = data
        } catch (error) {
          logError('Failed to fetch source', error)
        }
      }
      setSourceData(res)
      setIsBusy(false)
    }
    f()
  }, [sourceConfigs])

  useEffect(() => {
    function createMergedItem(id: string, scores: mergedScoreMap): IDataSourceItem | undefined {
      const _sourceItem = _.chain(sourceConfigs)
        .filter((src) => src.sourceID === 'catalog' || src.sourceID === 'localFile')
        .map(({sourceID, key}) => _.cloneDeep(_.get(sourceData[sourceID][key], id)))
        .reduce(_.merge)
        .value()
      if (!_sourceItem) return
      if (scores[id]) _sourceItem._merged = scores[id]
      return _sourceItem
    }

    const _allIDs = _.chain(sourceConfigs)
      .map(({sourceID, key}) => sourceData[sourceID][key])
      .flatMap(_.keys)
      .uniq()
      .value()

    /**
     * prepare scores for merging
     */
    const _mergeScores: mergedScoreMap = {}
    _.chain(sourceConfigs)
      .forEach(({sourceID, key}) => {
        if (sourceID === 'similar') {
          const _data = sourceData[sourceID][key]
          if (!_data) return
          _.chain(_data)
            .toPairs()
            .forEach(([k, v]) => {
              const _score = _.get(v, 'score')
              if (_score === undefined) return
              _.set(_mergeScores, `["${k}"]["similar"]["${key}"]`, _score)
              _.set(_mergeScores, `["${key}"]["similar"]["${k}"]`, _score)
            })
            .value()
        } else if (sourceID === 'ask') {
          const _data = sourceData[sourceID][key]
          if (!_data) return
          _.chain(_data)
            .toPairs()
            .forEach(([k, v]) => {
              const _score = _.get(v, 'score')
              if (_score === undefined) return
              _.set(_mergeScores, `["${k}"]["ask"]["${key}"]`, _score)
              // _.set(_mergeScores, `["${key}"]["ask"]["${k}"]`, _score);
            })
            .value()
        } else if (sourceID === 'expression') {
          const _data = sourceData[sourceID][key]
          if (!_data) return
          _.chain(_data)
            .toPairs()
            .forEach(([k, v]) => {
              if (v === undefined) return
              _.set(_mergeScores, `["${k}"]["expression"]["${key}"]`, v)
              // _.set(_mergeScores, `["${key}"]["ask"]["${k}"]`, _score);
            })
            .value()
        }
      })
      .value()

    const _merged = _.chain(_allIDs)
      .map((id) => createMergedItem(id, _mergeScores))
      .compact()
      .value()

    setMergedItems(_merged)
  }, [sourceData])

  const mergedItemsStats = useDataSourceStats(mergedItems)

  function replaceSources(sources: IDataSource[]) {
    setSourceConfigs(sources)

    // Update catalog selection through CatalogSelectionContext
    const catalogSource = sources.find((src) => src.sourceID === 'catalog')
    if (catalogSource && catalogSource.args?.catalogName) {
      setActiveCatalogName(catalogSource.args.catalogName)
    } else {
      // Clear activeCatalogName when switching to non-catalog sources (e.g., local file)
      setActiveCatalogName('')
    }
  }

  function addSource(source: IDataSource) {
    setSourceConfigs((prev) => {
      const exists = _.chain(prev)
        .map((s) => s.sourceID === source.sourceID && _.isEqual(s.args, source.args))
        .reduce((a, b) => a || b)
        .value()
      if (exists) {
        return prev
      }
      return [...prev, source]
    })
  }

  function showDialog() {
    setIsDialogOpen(true)
  }

  function skipLoadingSources() {
    setSkipIsBusy(true)
    setIsDialogOpen(false)
    setIsBusyState(false)
  }

  async function fetchSourceConfig(
    config: IDataSource,
    ignoreCache: boolean = false
  ): ReturnType<IDataSource['fetch']> {
    const {sourceID, key, fetch} = config
    if (!ignoreCache && sourceData[sourceID][key]) {
      return sourceData[sourceID][key]
    }
    try {
      return await fetch()
    } catch (error) {
      logError('Failed to fetch source', error)
    }
  }

  function ensureFetch() {
    // DEPRECATED: This function no longer automatically loads catalog data.
    // Pages should use useCatalogData() hook instead for explicit loading.
    console.warn('ensureFetch() is deprecated. Use useCatalogData() hook instead.')
  }

  return (
    <DataSourcesContext.Provider
      value={{
        isBusy,
        isDialogOpen,
        mergedItems,
        mergedItemsStats,
        sourceConfigs,
        sourceData,
        activeCatalogName,
        addSource,
        replaceSources,
        showDialog,
        skipLoadingSources,
        ensureFetch,
      }}
    >
      <RequirePermission require={'auth_utils.access_to_all_catalogs'}>
        <DataSourceOverrideDialog onClose={() => setIsDialogOpen(false)} />
      </RequirePermission>
      <LegacyCatalogContextWrap>{children}</LegacyCatalogContextWrap>
      <Dimmer active={isBusy}>
        <Loader>Loading catalog</Loader>
      </Dimmer>
    </DataSourcesContext.Provider>
  )
}
